import React, { useEffect, useRef, useState } from "react";

/** Local-only UI. Never selected from a server message, HTML, or structured type. */
export default function FeedbackWidget({ onSubmit, onCancel }) {
  const [phase, setPhase] = useState("loading");
  const [helpful, setHelpful] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const submitting = useRef(false);
  const widgetRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setPhase("question"), 1200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    widgetRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [phase, error]);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!rating) return setError("Please select a rating first.");
    if (rating <= 3 && !comment.trim()) return setError("Feedback is required for ratings of 3 stars or below.");
    submitting.current = true;
    setPending(true);
    setError("");
    try {
      const result = await onSubmit(comment, rating);
      if (result === false) throw new Error("Submission failed");
      setPhase("submitted");
    } catch {
      setError("Sorry, there was an error submitting your feedback. Please try again.");
    } finally {
      submitting.current = false;
      setPending(false);
    }
  };

  return (
    <div className="message-row message-enter" ref={widgetRef}>
      <div className="bot-block feedback-widget">
        {phase === "loading" && <div className="typing-dots" aria-label="DIVA is typing"><span /><span /><span /></div>}
        {phase === "question" && <>
          <div className="feedback-question">Was I able to assist with your query?</div>
          <div className="feedback-buttons">
            {[true, false].map((value) => <button key={String(value)} type="button" onClick={() => { setHelpful(value); setPhase("form"); }}>{value ? "Yes" : "No"}</button>)}
          </div>
        </>}
        {phase === "form" && <>
          <div className="feedback-question">{rating > 0 ? (rating <= 3 ? "Please let me know how I can improve." : "Please share what worked well for you.") : (helpful ? "Please rate your experience and share your feedback." : "Please let me know how I can improve.")}</div>
          <form className="feedback-form" onSubmit={submit}>
            <div className="star-rating">
              <div className="rating-label">Rate your experience:</div>
              <div className="stars" role="group" aria-label="Rate your experience" onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((value) => <button key={value} className={`star${value <= (hover || rating) ? " is-selected" : ""}`} type="button" aria-label={`${value} ${value === 1 ? "star" : "stars"}`} aria-pressed={rating === value} disabled={pending} onMouseEnter={() => setHover(value)} onFocus={() => setHover(value)} onBlur={() => setHover(0)} onClick={() => setRating(value)}>★</button>)}
              </div>
            </div>
            <textarea className="feedback-textarea" aria-label="Feedback" placeholder="Type your feedback here..." value={comment} disabled={pending} onChange={(event) => setComment(event.target.value)} />
            {error && <div className="ticket-form-error" role="alert">{error}</div>}
            <div className="feedback-actions"><button className="feedback-submit-btn" type="submit" disabled={pending}>{pending ? "Submitting…" : "Submit Feedback"}</button><button className="feedback-cancel" type="button" disabled={pending} onClick={onCancel}>Cancel feedback</button></div>
          </form>
        </>}
        {phase === "submitted" && <div>Thank you for your {rating}-star rating and feedback. I'll work on improving my responses.</div>}
        {phase !== "submitted" && phase !== "form" && <div className="feedback-cancel-row"><button className="feedback-cancel" type="button" disabled={pending} onClick={onCancel}>Cancel feedback</button></div>}
      </div>
    </div>
  );
}
